/**
 * ForcePasswordChangeDialog
 * Dialog shown when user must change their password (e.g., invited parent)
 */
import { useState } from "react";
import { toast } from "sonner";
import { axiosInstance } from "../../config/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function ForcePasswordChangeDialog({ open, user, onPasswordChanged }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Kata laluan mesti sekurang-kurangnya 6 aksara");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Kata laluan tidak sepadan");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axiosInstance.put('/parent/change-password', {
        new_password: newPassword,
        is_first_time: true
      });
      
      const updatedUser = { ...user, must_change_password: false };
      toast.success("Kata laluan berjaya ditukar!");
      onPasswordChanged(updatedUser);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menukar kata laluan");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-center justify-center">
            <Lock className="w-5 h-5 text-amber-500" />
            Tukar Kata Laluan
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
            <p className="text-sm text-amber-700 font-medium">
              Untuk keselamatan akaun anda, sila tukar kata laluan sekarang.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Kata Laluan Baru
              </label>
              <div className="relative">
                <input
                  data-testid="new-password-input"
                  type={showNewPassword ? "text" : "password"}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none pr-12"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Masukkan kata laluan baru"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sahkan Kata Laluan
              </label>
              <input
                data-testid="confirm-password-input"
                type="password"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Masukkan semula kata laluan"
              />
            </div>
            
            <p className="text-xs text-gray-500">
              Kata laluan mestilah sekurang-kurangnya 6 aksara
            </p>
            
            <Button
              data-testid="change-password-submit"
              type="submit"
              className="w-full bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white py-3 rounded-xl font-bold"
              disabled={changingPassword}
            >
              {changingPassword ? "Menukar..." : "Tukar Kata Laluan"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
